import { useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  ComparisonLegend,
  HorizontalStackedGroupBarChart,
  HorizontalStackedGroupBarChartProps,
  InputField,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  TIMP_CHART_COLORS,
  useBarLegendState,
  useHorizontalStackedGroupBarChartPngExport,
  usePngExport
} from '@jana-technology/platform-ui'

import { SortChart } from '$/types'
import { useAppSelector } from 'app/hooks'
import { useBaselineInfoQuery } from 'app/services/api/baselines'
import { useGetQRAAssessmentQuery } from 'app/services/api/qra/assessments'
import {
  IQRAGraphCsvExportParams,
  useExportQRARiskByRouteGraphMutation
} from 'app/services/api/qra/graphs'
import { useExportJobContext } from 'common/contexts/useExportJobContext'
import {
  formatNumberWithCommas,
  formatWithCommasOrExponential,
  getChartLabelBasedOnRiskUnit,
  makeRUoptions,
  selectDataLabel
} from 'common/utils/chart/chartUtils'

import {
  IQRARiskByRouteChartObject,
  IQRAThreatChartGrouping
} from 'features/RiskManagement/Components/Graphs'
import GraphContainerCard from 'features/RiskManagement/Components/Graphs/Components/GraphContainerCard'
import { selectRiskUnit } from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'

import { getChartCsvExportQueryParams, sortChartRows } from './Chart.helpers'

const CHART_TITLE = 'Risk By Route – Top 20'
const EXPORT_UNAVAILABLE_MESSAGE =
  'Chart image export is unavailable right now.'

const CHART_COLORS = [...TIMP_CHART_COLORS]
const formatterOptions = makeRUoptions('ru')

type RiskByRouteDatum = {
  category: string
  routeId: number
  stateKey: string
  stateLabel: string
  totalRisk: number
  totalLength: number
  tooltipValues: Record<string, number>
  [key: string]: string | number | Record<string, number>
}

interface IRiskByLineChart {
  showLoading: boolean
  chart: IQRARiskByRouteChartObject
  routeNavigationBasePath?: string
  csvExportParams?: IQRAGraphCsvExportParams
}

type ExportableHorizontalStackedGroupBarChartProps<D extends object> =
  HorizontalStackedGroupBarChartProps<D> & {
    displayComparisonLegendOnExport?: boolean
  }

const ExportableHorizontalStackedGroupBarChart = <D extends object>({
  displayComparisonLegendOnExport = false,
  ...props
}: ExportableHorizontalStackedGroupBarChartProps<D>) => {
  const exportColors = props.legendState?.activeColors ?? props.colors
  const exportDataKeys = props.legendState?.activeDataKeys ?? props.dataKeys
  const parentRef = useHorizontalStackedGroupBarChartPngExport({
    ...props,
    colors: exportColors,
    dataKeys: exportDataKeys,
    displayComparisonLegend: displayComparisonLegendOnExport
  })

  return (
    <div ref={parentRef} className='h-full w-full min-w-0'>
      <HorizontalStackedGroupBarChart<D> {...props} />
    </div>
  )
}

const getStateKeys = (chart: IQRARiskByRouteChartObject) => {
  const stateKeys: string[] = []

  for (const route of chart.routes) {
    for (const category of route.categoriesResult) {
      for (const value of category.values) {
        if (!stateKeys.includes(value.key)) {
          stateKeys.push(value.key)
        }
      }
    }
  }

  return stateKeys
}

const getGroupingValue = (
  grouping: IQRAThreatChartGrouping,
  categoriesByKey: Map<
    string,
    IQRARiskByRouteChartObject['routes'][number]['categoriesResult'][number]
  >,
  stateKey: string
) =>
  grouping.members.reduce(
    (total, memberKey) =>
      total +
      (categoriesByKey
        .get(memberKey)
        ?.values.find((value) => value.key === stateKey)?.value ?? 0),
    0
  )

const calculateTotalRisk = (
  datum: RiskByRouteDatum,
  groupings: IQRAThreatChartGrouping[],
  legendState: ReturnType<typeof useBarLegendState>
) =>
  groupings
    .filter((grouping) => legendState.activeDataKeys.includes(grouping.label))
    .reduce(
      (total, grouping) => total + (datum.tooltipValues[grouping.label] ?? 0),
      0
    )

const RiskByRouteTooltip = ({
  datum,
  formatLength,
  formatValue,
  groupings,
  routeData,
  legendState
}: {
  datum: RiskByRouteDatum
  formatLength: (value: number) => string
  formatValue: (value: number) => string
  groupings: IQRAThreatChartGrouping[]
  routeData: RiskByRouteDatum[]
  legendState: ReturnType<typeof useBarLegendState>
}) => {
  const showScenarioLabels = routeData.length > 1
  const ACTIVE_COLORS = legendState.activeColors

  if (showScenarioLabels) {
    return (
      <div className='px-2 py-1 leading-6'>
        <h4 className='font-semibold'>{datum.category}</h4>
        <table className='border-collapse'>
          <thead>
            <tr>
              <th className='pl-4 pr-4 text-left font-semibold' scope='col'>
                By Threat
              </th>
              {routeData.map((routeDatum) => (
                <th
                  key={routeDatum.stateKey}
                  className='pl-4 text-right font-semibold'
                  scope='col'
                >
                  {routeDatum.stateLabel}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groupings
              .filter((grouping) =>
                legendState.activeDataKeys.includes(grouping.label)
              )
              .map((grouping, index) => (
                <tr key={grouping.key}>
                  <th className='pl-4 pr-4 text-left font-normal' scope='row'>
                    <span className='flex items-center gap-1'>
                      <span
                        aria-label={`${grouping.label} color indicator`}
                        className='size-3 shrink-0 rounded-sm'
                        style={{
                          backgroundColor:
                            ACTIVE_COLORS[index % ACTIVE_COLORS.length]
                        }}
                      />
                      <span>{grouping.label}</span>
                    </span>
                  </th>
                  {routeData.map((routeDatum) => (
                    <td
                      key={`${grouping.key}-${routeDatum.stateKey}`}
                      className='pl-4 text-right tabular-nums'
                    >
                      {formatValue(
                        routeDatum.tooltipValues[grouping.label] ?? 0
                      )}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
          <tfoot>
            <tr className='font-semibold'>
              <th className='pr-4 text-left' scope='row'>
                Total Risk
              </th>
              {routeData.map((routeDatum) => (
                <td
                  key={`total-${routeDatum.stateKey}`}
                  className='pl-4 text-right tabular-nums'
                >
                  {formatValue(
                    calculateTotalRisk(routeDatum, groupings, legendState)
                  )}
                </td>
              ))}
            </tr>
            <tr className='font-semibold'>
              <th className='pr-4 text-left' scope='row'>
                Total Length
              </th>
              <td
                className='pl-4 text-right tabular-nums'
                colSpan={routeData.length}
              >
                {formatLength(datum.totalLength)} ft
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    )
  }

  return (
    <div className='px-2 py-1 leading-6'>
      <h4 className='font-semibold'>{datum.category}</h4>
      <div className='pl-4 font-semibold'>By Threat</div>
      {groupings
        .filter((grouping) =>
          legendState.activeDataKeys.includes(grouping.label)
        )
        .map((grouping, index) => (
          <div key={grouping.key} className='flex justify-between gap-4 pl-4'>
            <span className='flex items-center gap-1'>
              <span
                aria-label={`${grouping.label} color indicator`}
                className='size-3 shrink-0 rounded-sm'
                style={{
                  backgroundColor: ACTIVE_COLORS[index % ACTIVE_COLORS.length]
                }}
              />
              <span>{grouping.label}</span>
            </span>
            <span className='tabular-nums'>
              {formatValue(datum.tooltipValues[grouping.label] ?? 0)}
            </span>
          </div>
        ))}
      <div className='flex justify-between gap-4 font-semibold'>
        <span>Total Risk</span>
        <span className='tabular-nums'>
          {formatValue(calculateTotalRisk(datum, groupings, legendState))}
        </span>
      </div>
      <div className='flex justify-between gap-4 font-semibold'>
        <span>Total Length</span>
        <span className='tabular-nums'>
          {formatLength(datum.totalLength)} ft
        </span>
      </div>
    </div>
  )
}

const RiskByLineChart = ({
  showLoading,
  chart,
  routeNavigationBasePath,
  csvExportParams
}: IRiskByLineChart) => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const riskUnit = useAppSelector(selectRiskUnit)
  const [sortBy, setSortBy] = useState<SortChart>('risk-desc')
  const { assessmentId } = useParams()
  const { currentData: currentAssessment } = useGetQRAAssessmentQuery(
    assessmentId ?? skipToken
  )

  const { downloadImage, isReady: isChartPngExportReady } = usePngExport()

  const groupings = useMemo(
    () => chart.groupingConfig[0]?.groupings ?? [],
    [chart.groupingConfig]
  )
  const dataKeys = useMemo(
    () => groupings.map((grouping) => grouping.label),
    [groupings]
  )
  const legendState = useBarLegendState({
    colors: CHART_COLORS,
    dataKeys
  })

  const disableExportMenuItems = legendState.activeDataKeys.length === 0
  const stateKeys = getStateKeys(chart)
  const rows = chart.routes.map((route, originalIndex) => ({
    category: route.name,
    originalIndex,
    primaryValue: route.routeTotalRiskBaseline
  }))
  const visibleRoutes = sortChartRows(rows, sortBy)
    .slice(0, 20)
    .map((row) => chart.routes[row.originalIndex])
    .filter((route) => route != null)
  const data = visibleRoutes.flatMap<RiskByRouteDatum>((route) => {
    const categoriesByKey = new Map(
      route.categoriesResult.map((category) => [category.key, category])
    )

    return stateKeys.map((stateKey) => {
      const tooltipValues: Record<string, number> = {}
      const computedTotalRisk = route.categoriesResult.reduce(
        (total, category) =>
          total +
          (category.values.find((value) => value.key === stateKey)?.value ?? 0),
        0
      )

      const isBaseline = stateKey.toLowerCase() === 'baseline'
      let totalRisk = computedTotalRisk

      if (isBaseline) {
        totalRisk = route.routeTotalRiskBaseline
      } else if (stateKeys.length === 2) {
        totalRisk = route.routeTotalRiskScenario ?? computedTotalRisk
      }

      const datum: RiskByRouteDatum = {
        category: route.name,
        routeId: route.id,
        stateKey,
        stateLabel: selectDataLabel({ key: stateKey, value: 0 }),
        totalRisk,
        totalLength: route.pipeLength ?? 0,
        tooltipValues
      }

      for (const grouping of groupings) {
        const value = getGroupingValue(grouping, categoriesByKey, stateKey)
        datum[grouping.label] = value
        tooltipValues[grouping.label] = value
      }

      return datum
    })
  })

  const hasComparisonSeries = stateKeys.length > 1
  const comparisonLabels = stateKeys.map((stateKey) =>
    selectDataLabel({ key: stateKey, value: 0 })
  )

  const hasCsvExportData = csvExportParams != null && dataKeys.length > 0

  const formatValue = (value: number) =>
    formatWithCommasOrExponential(value, formatterOptions)
  const formatTooltipValue = (value: number) =>
    formatNumberWithCommas(value, { maximumFractionDigits: 3 })

  const handleExportChartAsPng = async () => {
    if (!isChartPngExportReady) {
      toast.error(EXPORT_UNAVAILABLE_MESSAGE)

      return
    }

    const assessmentSuffix = currentAssessment?.name
      ? `_${currentAssessment.name}`
      : ''

    try {
      await downloadImage({
        fileName: `Risk_By_Route${assessmentSuffix}`,
        options: { title: CHART_TITLE }
      })
    } catch {
      toast.error(EXPORT_UNAVAILABLE_MESSAGE)
    }
  }

  const { currentData: baseline } = useBaselineInfoQuery('current')

  const [triggerExportRiskByRouteGraph] = useExportQRARiskByRouteGraphMutation()

  const { createDataExportJob } = useExportJobContext()

  const getExportFileName = () => {
    let fileName = 'Risk_By_Route'

    if (currentAssessment?.name) {
      fileName += `_${currentAssessment.name}`
    }

    return fileName
  }

  const handleExportChartAsCsv = () => {
    if (!hasCsvExportData || disableExportMenuItems) {
      return
    }

    const fileName = getExportFileName()
    const baselineId = baseline?.id
    const enabledThreatLabels = legendState.activeDataKeys

    if (csvExportParams) {
      createDataExportJob(
        triggerExportRiskByRouteGraph,
        getChartCsvExportQueryParams({
          sortBy,
          csvExportParams,
          assessmentId,
          baselineId,
          enabledThreatLabels
        }),
        fileName,
        '.csv'
      )
    }
  }

  return (
    <GraphContainerCard
      title={CHART_TITLE}
      optionsComponent={
        <div className='flex items-center gap-2'>
          {hasComparisonSeries && (
            <ComparisonLegend labels={comparisonLabels} />
          )}
          <CardActions sortBy={sortBy} setSortBy={setSortBy} />
        </div>
      }
      actionMenuItems={[
        {
          children: 'Export chart as image',
          disabled: disableExportMenuItems,
          onSelect: () => void handleExportChartAsPng()
        },
        {
          children: 'Export chart as data',
          disabled: !hasCsvExportData || disableExportMenuItems,
          onSelect: () => handleExportChartAsCsv()
        }
      ]}
    >
      {showLoading ? (
        <div className='flex h-full items-center justify-center text-center'>
          <Spinner label='Loading...' size='medium' />
        </div>
      ) : (
        <ExportableHorizontalStackedGroupBarChart
          height={hasComparisonSeries ? 350 : 390}
          barWidth={hasComparisonSeries ? 8 : 16}
          groupInnerGap={4}
          chartName={CHART_TITLE}
          dataKeys={dataKeys}
          data={data}
          hoverAll={true}
          xAccessor={(datum) => datum.category}
          x1Accessor={
            hasComparisonSeries ? (datum) => datum.stateLabel : undefined
          }
          xAxisLabel={getChartLabelBasedOnRiskUnit(riskUnit)}
          yAxisLabel='Route'
          xTicks={3}
          xTickFormatter={formatValue}
          onBarClick={
            routeNavigationBasePath == null
              ? undefined
              : ({ datum }) => {
                  navigate(
                    `${routeNavigationBasePath}/${datum.routeId}${search}`
                  )
                }
          }
          renderTooltip={({ datum }) => (
            <RiskByRouteTooltip
              datum={datum}
              formatLength={formatTooltipValue}
              formatValue={formatValue}
              groupings={groupings}
              routeData={data.filter(
                (routeDatum) => routeDatum.routeId === datum.routeId
              )}
              legendState={legendState}
            />
          )}
          withScroll={true}
          legendAnchor='container-start'
          legendState={legendState}
          withLegend={true}
          noLabelWithComparisonLegend={false}
          displayComparisonLegend={false}
          displayComparisonLegendOnExport={hasComparisonSeries}
          colors={CHART_COLORS}
        />
      )}
    </GraphContainerCard>
  )
}

const CardActions = ({
  sortBy,
  setSortBy
}: {
  sortBy: SortChart
  setSortBy: (value: SortChart) => void
}) => (
  <InputField direction='row' className='p-4 text-sm'>
    <Label htmlFor='risk-by-route-chart-sort-select'>Sort:</Label>
    <Select value={sortBy} onValueChange={setSortBy}>
      <SelectTrigger id='risk-by-route-chart-sort-select' className='h-8 w-36'>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className='text-sm'>
        <SelectItem value='risk-asc'>Risk ↑</SelectItem>
        <SelectItem value='risk-desc'>Risk ↓</SelectItem>
        <SelectItem value='alphabetical-asc'>Alphabetical ↑</SelectItem>
        <SelectItem value='alphabetical-desc'>Alphabetical ↓</SelectItem>
      </SelectContent>
    </Select>
  </InputField>
)

export default RiskByLineChart
